import { html, LitElement } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from '../../../shared/services/debug.service.js';
import { style } from './personal-edit.style.js';
import { validateForm } from '../../../shared/decorators/validate-form.decorator.js';

import './edit-name/edit-name.component.js';
import './edit-identity/edit-identity.component.js';
import './edit-phone/edit-phone.component.js';
import './edit-email/edit-email.component.js';
import { ApplicantsService } from '../../../services/applicants.service.js';
import { dobFormatter } from '../../../shared/formatters/dob.formatter.js';
import { phoneNumberFormatter } from '../../../shared/formatters/phoneNumber.formatter.js';
import { REFI_PERSONAL_CONST } from '../../../shared/constants/app.constants.js';
import { TextOptInService } from '../../../services/text-opt-in.service.js';
import { stratum } from '../../../../constants/stratum.js';

class RefiPersonalEdit extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiPersonalEdit');
    this.validity = {};
    this.retryCount = 0;
    this.isLoadng = false;
  }

  static properties = {
    isLoading: { type: Boolean },
    eAppId: { type: String },
    applicantId: { type: String },
    applicant: { type: Object, reflect: true },
    updatedApplicant: { type: Object, reflect: true },
    textOptInError: { type: Boolean, reflect: true },
  };

  static styles = [style];

  firstUpdated() {
    stratum.publishTag(TagKey.VIEW_PERSONAL_CHECKOUT);
  }

  render() {
    return html`
      <form id="personal-edit-form" @submit="${this.onSubmit}" novalidate>
        <refi-edit-name
          @validity="${this.updateValidity}"
          .applicant="${this.updatedApplicant}"
          @unsaved-fields="${this.dispatchUnsavedFieldsEvent}"
        ></refi-edit-name>
        <refi-edit-identity
          @validity="${this.updateValidity}"
          .applicant="${this.updatedApplicant}"
          @unsaved-fields="${this.dispatchUnsavedFieldsEvent}"
        ></refi-edit-identity>
        <refi-edit-phone
          @validity="${this.updateValidity}"
          .applicant="${this.updatedApplicant}"
          .textOptInError="${this.textOptInError && !this.isLoading}"
          @unsaved-fields="${this.dispatchUnsavedFieldsEvent}"
        ></refi-edit-phone>
        <refi-edit-email
          @validity="${this.updateValidity}"
          .applicant="${this.updatedApplicant}"
          @unsaved-fields="${this.dispatchUnsavedFieldsEvent}"
        >
        </refi-edit-email>
        <div class="button-container">
          <div class="desktop-order">
            <fs-button
              id="cancel-personal-edit"
              class="cancel-button"
              variant="secondary"
              type="button"
              ?disabled="${this.isLoading}"
              @click="${this.onCancel}"
              >Cancel
            </fs-button>
            <fs-button
              id="update-personal"
              class="update-button"
              type="submit"
              variant="primary"
              @click="${() =>
                stratum.publishTag(
                  TagKey.PERSONAL_CHECKOUT_UPDATE_PERSONAL_CLICKED
                )}"
              ?loading="${this.isLoading}"
              >Update Personal
            </fs-button>
          </div>
          <div class="mobile-order">
            <fs-button
              id="update-personal"
              class="update-button"
              type="submit"
              variant="primary"
              @click="${() =>
                stratum.publishTag(
                  TagKey.PERSONAL_CHECKOUT_UPDATE_PERSONAL_CLICKED
                )}"
              ?loading="${this.isLoading}"
              >Update Personal
            </fs-button>
            <fs-button
              id="cancel-personal-edit"
              class="cancel-button"
              variant="secondary"
              type="button"
              ?disabled="${this.isLoading}"
              @click="${this.onCancel}"
              >Cancel
            </fs-button>
          </div>
        </div>
      </form>
    `;
  }

  dispatchUnsavedFieldsEvent() {
    this.debug.log('edit unsaved fields event');
    this.dispatchEvent(
      new CustomEvent('dispatch-unsaved-missing-fields', {
        bubbles: true,
        composed: true,
        detail: {
          actionName: 'unsaved',
        },
      })
    );
  }

  updateValidity(ev) {
    this.validity[ev.detail.name] = ev.detail.isValid;
  }

  @validateForm()
  async onSubmit() {
    let valid = true;
    this.debug.log(this.validity);
    Object.values(this.validity).forEach((validityValue) => {
      if (!validityValue) {
        valid = false;
      }
    });

    if (valid) {
      if (
        JSON.stringify(this.applicant) === JSON.stringify(this.updatedApplicant)
      ) {
        this.dispatchEvent(new CustomEvent('personal-edit-success'));
      } else {
        if (
          this.updatedApplicant.phoneType === 'MOBILE' &&
          this.updatedApplicant.hasTcpaConsent !== null
        ) {
          try {
            await TextOptInService.saveTextOptIn(
              phoneNumberFormatter(this.updatedApplicant.phoneNumber),
              this.updatedApplicant.hasTcpaConsent,
              this.updatedApplicant.phoneType.toLowerCase()
            );
            stratum.publishTag(
              TagKey.MOBILE_NUMBER_MODULE_TCPA_CONSENT_SAVED_TO_CUSTOMER_CONTACT
            );
            this.textOptInError = false;
          } catch (e) {
            try {
              const errorMessage = await e.json();
              if (errorMessage?.id === 5001) {
                stratum.publishTag(
                  TagKey.MOBILE_NUMBER_MODULE_TCPA_ALREADY_ASSOCIATED_ERROR_MESSAGE
                );
                this.textOptInError = true;
              } else {
                this.dispatchEvent(new CustomEvent('personal-edit-fail'));
                return;
              }
            } catch (nonHttpResponseError) {
              this.dispatchEvent(new CustomEvent('personal-edit-fail'));
              return;
            }
          }
        }

        const applicantUpdate = {
          firstName: this.updatedApplicant.firstName,
          lastName: this.updatedApplicant.lastName,
          dateOfBirth: dobFormatter(this.updatedApplicant.dateOfBirth),
          phoneNumber: phoneNumberFormatter(this.updatedApplicant.phoneNumber),
          primaryPhoneType: this.updatedApplicant.phoneType,
          emailAddress: this.updatedApplicant.emailAddress,
        };
        this.retryCount = 0;
        this.updateApplicant(applicantUpdate);
      }
    }
  }

  async updateApplicant(applicantUpdate) {
    try {
      this.isLoading = true;
      await ApplicantsService.updateApplicantInformation(
        this.eAppId,
        this.applicantId,
        applicantUpdate
      ).then(() => {
        sessionStorage.setItem(
          REFI_PERSONAL_CONST.FIRST_NAME_SESSION_KEY,
          applicantUpdate.firstName
        );
        this.tagFieldChangesOnSubmit();
        stratum.publishTag(
          TagKey.PERSONAL_CHECKOUT_PERSONAL_UPDATES_SAVE_SUCCESSFUL
        );
        this.applicant = JSON.parse(JSON.stringify(this.updatedApplicant));
        if (!this.textOptInError) {
          this.dispatchEvent(new CustomEvent('personal-edit-success'));
        }
        this.isLoading = false;
      });
    } catch (err) {
      if (this.retryCount < 2) {
        this.retryCount += 1;
        this.updateApplicant(applicantUpdate);
      } else {
        this.dispatchEvent(new CustomEvent('personal-edit-fail'));
        stratum.publishTag(
          TagKey.PERSONAL_CHECKOUT_PERSONAL_UPDATES_SAVE_FAILED
        );
        this.isLoading = false;
      }
    }
  }

  tagFieldChangesOnSubmit() {
    Object.keys(this.updatedApplicant).forEach((field) => {
      if (this.updatedApplicant[field] !== this.applicant[field]) {
        this.debug.log(`${field} field changed`);
        stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
          replacements: {
            value: `iconic-${field}`,
          },
        });
      }
    });
  }

  onCancel() {
    stratum.publishTag(TagKey.PERSONAL_CHECKOUT_CANCEL_PERSONAL_CLICKED);
    this.dispatchEvent(new CustomEvent('cancel-personal-edit'));
  }
}

customElements.define('refi-personal-edit', RefiPersonalEdit);
